/**
 * A shop's or store's opening hours (backend B3.4 `OpeningHours`) as a week
 * the page shows and the `OpeningHoursSpecification` its JSON-LD carries, from
 * the same rows so the two never disagree.
 *
 * The backend's rules: 24-hour "HH:MM" local time; `closes` earlier than
 * `opens` (or "00:00") runs past midnight; "24:00" is midnight; several rows
 * for one day are split shifts; a day in no row is closed; `[]` = nothing
 * entered (then nothing is shown and no hours are claimed). A row the backend
 * would refuse (unknown day, bad time) is skipped here rather than guessed.
 * Pure (no React, no `window`).
 */
import type { OpeningHours, Weekday } from '@/types';

/** Monday first, as the backend stores them. */
export const WEEKDAYS: readonly Weekday[] = Object.freeze(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN']);

const OPENS = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const CLOSES = /^(?:(?:[01]\d|2[0-3]):[0-5]\d|24:00)$/;

export interface TimeRange {
  opens: string;
  closes: string;
}

export interface DayHours {
  day: Weekday;
  /** In opening order; `[]` = closed that day. */
  ranges: TimeRange[];
  /** Open all day: one range 00:00 – 24:00. */
  allDay: boolean;
}

/** The rows the backend's rules accept: known days, valid times, opens ≠ closes. */
function validRows(rows: readonly OpeningHours[] | null | undefined): OpeningHours[] {
  if (!Array.isArray(rows)) return [];
  return rows.filter((row: OpeningHours | null | undefined): row is OpeningHours => {
    if (!row || typeof row.opens !== 'string' || typeof row.closes !== 'string') return false;
    const days: readonly unknown[] = Array.isArray(row.days) ? row.days : [];
    return (
      days.some((day) => WEEKDAYS.includes(day as Weekday)) &&
      OPENS.test(row.opens) &&
      CLOSES.test(row.closes) &&
      row.opens !== row.closes
    );
  });
}

/**
 * Monday to Sunday with each day's ranges, or null when there is no valid row
 * (nothing entered: say nothing, rather than "Closed" seven times). An
 * overnight range is listed under the day it opens.
 */
export function weeklyHours(rows: readonly OpeningHours[] | null | undefined): DayHours[] | null {
  const valid = validRows(rows);
  if (valid.length === 0) return null;
  return WEEKDAYS.map((day) => {
    const ranges = valid
      .filter((row) => row.days.includes(day))
      .map(({ opens, closes }) => ({ opens, closes }))
      .sort((a, b) => a.opens.localeCompare(b.opens));
    const unique = ranges.filter(
      (range, i) => i === 0 || range.opens !== ranges[i - 1].opens || range.closes !== ranges[i - 1].closes,
    );
    const allDay = unique.length === 1 && unique[0].opens === '00:00' && unique[0].closes === '24:00';
    return { day, ranges: unique, allDay };
  });
}

const SCHEMA_DAYS: Readonly<Record<Weekday, string>> = Object.freeze({
  MON: 'https://schema.org/Monday',
  TUE: 'https://schema.org/Tuesday',
  WED: 'https://schema.org/Wednesday',
  THU: 'https://schema.org/Thursday',
  FRI: 'https://schema.org/Friday',
  SAT: 'https://schema.org/Saturday',
  SUN: 'https://schema.org/Sunday',
});

/**
 * One `OpeningHoursSpecification` per valid row (its days Monday first), or
 * undefined for none. Midnight as a closing time ("24:00", or "00:00") is
 * written "23:59", the form Google documents for "until midnight" and "open 24
 * hours" (00:00 – 23:59); a range past midnight keeps its closing time
 * ("18:00" – "02:00"), which schema.org reads as the next day.
 */
export function openingHoursSpecification(rows: readonly OpeningHours[] | null | undefined): object[] | undefined {
  const valid = validRows(rows);
  if (valid.length === 0) return undefined;
  return valid.map((row) => ({
    '@type': 'OpeningHoursSpecification',
    dayOfWeek: WEEKDAYS.filter((day) => row.days.includes(day)).map((day) => SCHEMA_DAYS[day]),
    opens: row.opens,
    closes: row.closes === '24:00' || row.closes === '00:00' ? '23:59' : row.closes,
  }));
}
